/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BackgroundContainer, ResponsiveContainer, SectionContainer, SectionHeading } from "@components/elements";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { VStack } from "@panda/jsx";

import { AccordionTierCard } from "./AccordionTierCard.molecule";
import { tiersListStyles } from "./AccordionTierCard.styles";

import type { AccordionTierCardsSectionFragment } from "@cosmos/hygraph-schema";

export type AccordionTierCardsSectionProps = AccordionTierCardsSectionFragment;

/**
 * Section-level wrapper for a stack of `AccordionTierCard`s. Mirrors the
 * `AccordionFeatureCardsSection` shape so it slots into `DynamicSections` cleanly
 * via spread.
 */
export const AccordionTierCardsSection = ({
	config,
	items,
}: AccordionTierCardsSectionProps): React.JSX.Element | null => {
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(items?.length);
	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack alignItems="stretch" gap="eds.dimension.48">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<div className={tiersListStyles}>
								{items.map((item, i) => (
									<AccordionTierCard key={item.id || `tier-${i}`} index={i} {...item} />
								))}
							</div>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};

AccordionTierCardsSection.displayName = "AccordionTierCardsSection";
